import React, { useState, useEffect, useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSettingsDeleteAllData } from "src/model/settings";
import { ProfileType, SubscriptionTier } from "src/model_v2/client.schemas";
import { Button } from "src/components/ui/button";
import {
  Check,
  BarChart3,
  ChevronsUpDown,
  Sparkles,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import CheckCircleIcon from "src/assets/check_circle.svg?react";
import { cn } from "src/components/lib/utils";
import { functions, auth, analytics } from "src/util/firebase";
import { logEvent } from "firebase/analytics";
import { httpsCallable } from "firebase/functions";
import { deleteUser, updateProfile } from "firebase/auth";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/ui/command";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { useUserSettings } from "@/context/AppConfigContext";
import { getGetAppConfigQueryKey } from "@/model_v2/app-config";
import { useQueryClient } from "@tanstack/react-query";
import { appConfigLoader } from "@/util/query_client";
import { useUpdateSettings } from "@/model_v2/settings";
import { IANA_TIMEZONES } from "@/lib/timezone";
import ActionDialog from "@/components/ActionDialog";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";

const BASIC_FEATURES = [
  "1 Import Source",
  "3 Statement Uploads / Month",
  "Basic Stats",
  "Automatic Spread Detection",
];

const PRO_FEATURES = [
  "Unlimited Import Sources",
  "Unlimited Statement Uploads",
  "Broker Sync",
  "Technical Analysis Stats",
  "Fundamental Analysis Stats",
  "Daily Journal",
  "AI Insights to help you make better decisions",
];

export const Route = createFileRoute("/_app/settings")({
  component: SettingsPage,
  staticData: {
    title: "Settings",
  },
  loader: appConfigLoader,
});

const TimezoneCombobox: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-72 justify-between max-lg:w-full"
        >
          {value || "Select timezone..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0 max-lg:w-full" align="start">
        <Command>
          <CommandInput placeholder="Search timezone..." />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>
            <CommandGroup>
              {IANA_TIMEZONES.map((tz) => (
                <CommandItem
                  key={tz}
                  value={tz}
                  onSelect={(currentValue) => {
                    onChange(currentValue);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === tz ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {tz}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const SubscriptionTab: React.FC<{
  settings: {
    subscriptionTier?: SubscriptionTier | null | undefined;
  };
}> = ({ settings }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleCustomerPortalRedirect = async () => {
    setIsLoading(true);
    try {
      const functionRef = httpsCallable(
        functions,
        import.meta.env.VITE_STRIPE_CREATE_PORTAL_LINK_FUNCTION || "",
      );
      const { data } = await functionRef({
        returnUrl: window.location.origin,
        locale: "auto",
      });
      if (
        typeof data === "object" &&
        data !== null &&
        "url" in data &&
        typeof data.url === "string"
      ) {
        window.location.assign(data.url);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Error redirecting to customer portal:", error);
      alert(
        `An error occurred: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
    setIsLoading(false);
  };

  return (
    <div className="flex flex-col items-center">
      <div className="mt-6 flex w-full flex-col gap-5 text-black dark:text-white md:flex-row md:justify-center">
        <div className="inline-flex w-full flex-col items-center rounded-lg border border-border bg-card p-5 md:w-[305px]">
          <div className="flex items-center justify-center gap-2 rounded-[14px] border border-border bg-background p-5 shadow">
            <BarChart3 className="h-10 w-10" />
          </div>
          <div className="mt-3 text-2xl font-medium leading-loose">Basic</div>
          <div className="text-sm font-normal leading-tight text-muted-foreground">
            Free!
          </div>

          <div className="my-6 flex w-full flex-col gap-4">
            {BASIC_FEATURES.map((feature) => (
              <div key={feature} className="flex gap-4">
                <div>
                  <Check className="h-6 w-6" />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
          {settings.subscriptionTier === SubscriptionTier.Free && (
            <Button className="mt-auto" variant="outline">
              Current Plan
            </Button>
          )}
          {settings.subscriptionTier === SubscriptionTier.Pro && (
            <Button
              className="mt-auto"
              variant="outline"
              onClick={handleCustomerPortalRedirect}
            >
              Downgrade
            </Button>
          )}
        </div>
        <div className="inline-flex w-full flex-col items-center rounded-lg border-4 border-border border-purple4 bg-card p-5 md:w-[305px]">
          <div className="flex items-center justify-center gap-2 rounded-[14px] border border-border bg-background p-5 shadow">
            <Sparkles className="h-10 w-10" />
          </div>
          <div className="mt-3 text-2xl font-medium leading-loose">Pro</div>
          <div className="mt-3 flex flex-col items-center">
            {settings.subscriptionTier === SubscriptionTier.Pro ? (
              <div className="text-sm font-medium text-purple3">
                You're subscribed
              </div>
            ) : (
              <div className="text-center text-sm text-muted-foreground">
                No longer available for new subscriptions
              </div>
            )}
          </div>
          <div className="my-6 flex w-full flex-col gap-4">
            {PRO_FEATURES.map((feature) => (
              <div key={feature} className="flex gap-4">
                <div>
                  <CheckCircleIcon className="h-6 w-6" />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
          <div className="mt-auto">
            {settings.subscriptionTier === SubscriptionTier.Pro && (
              <Button
                variant="gradient"
                onClick={handleCustomerPortalRedirect}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Manage Subscription"
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="mt-6"></div>
    </div>
  );
};

const AccountTab: React.FC<{
  isDeleting: boolean;
  isDeletingAccount: boolean;
  handleDeleteAllData: () => void;
  handleDeleteAccount: () => void;
}> = ({
  isDeleting,
  isDeletingAccount,
  handleDeleteAllData,
  handleDeleteAccount,
}) => {
  const [displayName, setDisplayName] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (auth.currentUser?.displayName) {
      setDisplayName(auth.currentUser.displayName);
    }
  }, []);

  const handleUpdateDisplayName = async () => {
    if (!auth.currentUser) return;

    setIsUpdating(true);
    setError("");

    try {
      // Update the profile
      await updateProfile(auth.currentUser, {
        displayName: displayName,
      });

      // Force a token refresh to ensure the auth state is updated
      await auth.currentUser.getIdToken(true);

      // Emit a custom event that the sidebar can listen for
      const event = new CustomEvent("userProfileUpdated", {
        detail: { displayName },
      });
      window.dispatchEvent(event);

      setUpdateSuccess(true);

      // Auto-hide the checkmark after a few seconds
      setTimeout(() => {
        setUpdateSuccess(false);
      }, 2000);
    } catch (error) {
      console.error("Error updating display name:", error);
      setError(
        `Failed to update name: ${error instanceof Error ? error.message : String(error)}`,
      );
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="flex flex-col space-y-6">
      <div>
        <label className="mb-2 block text-sm font-medium">
          Display Name
          <br />
          <span className="text-xs text-muted-foreground">
            This is the name displayed throughout the app.
          </span>
        </label>
        <div className="flex items-center gap-2">
          <div className="flex w-72 max-lg:w-full">
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="rounded-r-none"
            />
            <Button
              variant="gradient"
              onClick={handleUpdateDisplayName}
              disabled={isUpdating || !displayName.trim()}
              className="rounded-l-none"
            >
              {isUpdating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Update"
              )}
            </Button>
          </div>
          {updateSuccess && <Check className="h-5 w-5 text-success" />}
        </div>
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      </div>

      <div className="mt-8">
        <div className="rounded-md border border-destructive/20 p-4 dark:border-destructive">
          <div className="mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <h3 className="text-lg font-medium">Danger Zone</h3>
          </div>
          <Separator className="mb-4 bg-destructive/20 dark:bg-destructive" />

          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium">
                Reset Account Data
                <br />
                <span className="text-xs text-muted-foreground">
                  This will delete all your data from Trader Sage but keep your
                  account. This action cannot be undone.
                </span>
              </label>
              <ActionDialog
                action={handleDeleteAllData}
                style="fit"
                dialogDescription="Are you sure you want to delete all your data? This action cannot be undone."
                acceptText="Reset"
              >
                <Button
                  variant="destructive"
                  disabled={isDeleting}
                  className="w-72 max-lg:w-full"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    "Reset Account"
                  )}
                </Button>
              </ActionDialog>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">
                Delete Account
                <br />
                <span className="text-xs text-muted-foreground">
                  This will permanently delete your account and all associated
                  data. This action cannot be undone.
                </span>
              </label>
              <ActionDialog
                action={handleDeleteAccount}
                style="fit"
                dialogDescription="Are you sure you want to permanently delete your account? All your data will be lost and this action cannot be undone."
              >
                <Button
                  variant="destructive"
                  disabled={isDeletingAccount}
                  className="w-72 max-lg:w-full"
                >
                  {isDeletingAccount ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting Account...
                    </>
                  ) : (
                    "Delete Account"
                  )}
                </Button>
              </ActionDialog>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OtherSettingsTab: React.FC<{
  settings: {
    timezone: string;
  };
  isTimezoneUpdating: boolean;
  isTimezoneUpdateSuccess: boolean;
  handleTimezoneChange: (value: string) => void;
}> = ({
  settings,
  isTimezoneUpdating,
  isTimezoneUpdateSuccess,
  handleTimezoneChange,
}) => {
  return (
    <div className="flex flex-col space-y-6">
      <div>
        <label className="mb-2 block text-sm font-medium">
          Default Timezone
          <br />
          <span className="text-xs text-muted-foreground">
            This controls the timezone used to show dates across the app.
          </span>
        </label>
        <div className="flex items-center gap-2">
          <TimezoneCombobox
            value={settings.timezone || "UTC"}
            onChange={handleTimezoneChange}
          />
          {isTimezoneUpdating && (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          )}
          {isTimezoneUpdateSuccess && (
            <Check className="h-5 w-5 text-success" />
          )}
        </div>
      </div>
    </div>
  );
};

function SettingsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const userSettings = useUserSettings();
  const { mutate: deleteAllData, isPending: isDeleting } =
    useSettingsDeleteAllData();
  const { mutate: updateSettings } = useUpdateSettings();

  const [settings, setSettings] = useState({
    timezone: "US/Eastern",
    scratchAmount: "",
    profileType: undefined as ProfileType | undefined,
    subscriptionTier: null as SubscriptionTier | null | undefined,
  });
  const [isTimezoneUpdating, setIsTimezoneUpdating] = useState(false);
  const [isTimezoneUpdateSuccess, setIsTimezoneUpdateSuccess] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  useEffect(() => {
    if (userSettings) {
      setSettings((prevSettings) => ({
        timezone: userSettings.timezone ?? prevSettings.timezone,
        scratchAmount: userSettings.scratch_amount?.toString() ?? "",
        profileType: userSettings.profile_type ?? undefined,
        subscriptionTier: userSettings.subscription_tier,
      }));
    }
  }, [userSettings]);

  const handleTimezoneChange = useCallback(
    (value: string) => {
      setSettings((prev) => ({ ...prev, timezone: value }));
      setIsTimezoneUpdating(true);
      setIsTimezoneUpdateSuccess(false);

      updateSettings(
        {
          data: {
            tz: value,
          },
        },
        {
          onSuccess: () => {
            setIsTimezoneUpdating(false);
            setIsTimezoneUpdateSuccess(true);

            // Auto-hide the checkmark after a few seconds
            setTimeout(() => {
              setIsTimezoneUpdateSuccess(false);
            }, 2000);

            queryClient.refetchQueries({
              queryKey: getGetAppConfigQueryKey(),
            });
          },
          onError: () => {
            setIsTimezoneUpdating(false);
          },
        },
      );
    },
    [updateSettings, queryClient],
  );

  const handleDeleteAllData = useCallback(() => {
    deleteAllData(undefined, {
      onSuccess: () => {
        queryClient.invalidateQueries({ refetchType: "all" });
      },
    });
  }, [deleteAllData, queryClient]);

  const handleDeleteAccount = useCallback(async () => {
    if (!auth.currentUser) return;

    // Track account deletion in Google Analytics
    logEvent(analytics, "account_deleted");

    setIsDeletingAccount(true);
    try {
      // First delete all user data
      const deleteDataPromise = new Promise<void>((resolve, reject) => {
        deleteAllData(undefined, {
          onSuccess: () => {
            queryClient.invalidateQueries({ refetchType: "all" });
            resolve();
          },
          onError: (error) => {
            reject(error);
          },
        });
      });

      // Wait for data deletion to complete
      await deleteDataPromise;

      // Then delete the user account
      await deleteUser(auth.currentUser);

      // Redirect to sign in page
      navigate({ to: "/signin" });
    } catch (error) {
      console.error("Error deleting account:", error);
      alert(
        `Failed to delete account: ${error instanceof Error ? error.message : String(error)}`,
      );
      setIsDeletingAccount(false);
    }
  }, [deleteAllData, queryClient, navigate]);

  return (
    <>
      <Card className="card mx-auto w-full p-6">
        <CardContent>
          <Tabs defaultValue="subscription">
            <TabsList className="max-w-full overflow-x-auto rounded-lg border p-0.5 dark:border-gray3 dark:border-opacity-5 dark:bg-black">
              <TabsTrigger
                className="h-full w-36 text-sm font-medium leading-none data-[state=active]:bg-purple2/10 dark:data-[state=active]:bg-medium-gray"
                value="subscription"
              >
                Subscription
              </TabsTrigger>
              <TabsTrigger
                className="h-full w-36 text-sm font-medium leading-none data-[state=active]:bg-purple2/10 dark:data-[state=active]:bg-medium-gray"
                value="account"
              >
                Account
              </TabsTrigger>
              <TabsTrigger
                className="h-full w-36 text-sm font-medium leading-none data-[state=active]:bg-purple2/10 dark:data-[state=active]:bg-medium-gray"
                value="other"
              >
                Other
              </TabsTrigger>
            </TabsList>
            <TabsContent value="subscription" className="mt-5">
              <SubscriptionTab settings={settings} />
            </TabsContent>
            <TabsContent value="account" className="mt-5">
              <AccountTab
                isDeleting={isDeleting}
                isDeletingAccount={isDeletingAccount}
                handleDeleteAllData={handleDeleteAllData}
                handleDeleteAccount={handleDeleteAccount}
              />
            </TabsContent>
            <TabsContent value="other" className="mt-5">
              <OtherSettingsTab
                settings={settings}
                isTimezoneUpdating={isTimezoneUpdating}
                isTimezoneUpdateSuccess={isTimezoneUpdateSuccess}
                handleTimezoneChange={handleTimezoneChange}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </>
  );
}
